import { apiRequest } from './http';
import type {
  ApiItemResponse,
  ApiListResponse,
  ExchangeDetails,
  ExchangeListQuery,
  ExchangeStatus,
  ExchangeSummary,
} from '../types/api';

function queryString(query: ExchangeListQuery): string {
  const search = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export async function listExchanges(query: ExchangeListQuery): Promise<ApiListResponse<ExchangeSummary>> {
  return apiRequest<ApiListResponse<ExchangeSummary>>(`/exchanges${queryString(query)}`);
}

// Statuses that represent unresolved exchanges needing operator attention -- what the DLQ /
// recovery views (ExchangesPage in recoveryMode) mean by "the recovery queue". Exported as the
// single source of truth so every place that needs "is this exchange a recovery candidate?"
// (the recovery queue page itself, and any KPI/summary that links to it) asks the same question
// the same way and can never silently drift apart.
const RECOVERY_QUEUE_STATUSES: ExchangeStatus[] = ['FAILED', 'WAITING_RETRY', 'DEAD_LETTERED'];

/**
 * The full set of exchanges an operator still needs to act on, across all unresolved statuses.
 * There's no time-window limit -- callers linking to /dlq (ExchangesPage recoveryMode) must show
 * the same count they promised, not a windowed approximation of it.
 */
export async function listRecoveryQueue(
  filters: Omit<ExchangeListQuery, 'status' | 'page' | 'pageSize'> = {},
): Promise<ApiListResponse<ExchangeSummary>> {
  const results = await Promise.all(
    RECOVERY_QUEUE_STATUSES.map((status) => listExchanges({ ...filters, page: 1, pageSize: 50, status })),
  );
  const rows = results
    .flatMap((result) => result.data)
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());
  return {
    data: rows,
    total: results.reduce((sum, result) => sum + result.total, 0),
    page: 1,
    pageSize: rows.length || 50,
  };
}

export async function getExchange(id: string): Promise<ExchangeDetails> {
  const response = await apiRequest<ApiItemResponse<ExchangeDetails>>(`/exchanges/${id}`);
  return response.data;
}

export async function requestExchangeRetry(id: string, reason?: string): Promise<ExchangeDetails> {
  const response = await apiRequest<ApiItemResponse<ExchangeDetails>>(`/exchanges/${id}/retry`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return response.data;
}

export async function deadLetterExchange(
  id: string,
  reason: string,
  options: { quarantineMode?: boolean; operatorComment?: string } = {},
): Promise<ExchangeDetails> {
  const response = await apiRequest<ApiItemResponse<ExchangeDetails>>(`/exchanges/${id}/dead-letter`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...options }),
  });
  return response.data;
}

export async function requestExchangeReplay(id: string, reason?: string): Promise<ExchangeDetails> {
  const response = await apiRequest<ApiItemResponse<ExchangeDetails>>(`/exchanges/${id}/replay`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return response.data;
}

export async function archiveExchange(id: string, reason: string): Promise<ExchangeDetails> {
  const response = await apiRequest<ApiItemResponse<ExchangeDetails>>(`/exchanges/${id}/archive`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return response.data;
}
